import { useState, useCallback, useRef } from "react";
import * as api from "../api";

// Incidencias del Servicio de asistencia de Odoo.
//
// No se guarda nada en Postgres: se lee de Odoo en vivo en cada consulta. Un
// ticket cambia de etapa constantemente y una copia local envejecería mal.
export function useHelpdesk(token, showToast) {
  const [tickets,  setTickets]  = useState([]);
  const [kanban,   setKanban]   = useState({ etapas: [], tickets: [], totales: {}, total: 0 });
  const [total,    setTotal]    = useState(0);
  const [meta,     setMeta]     = useState({ equipos: [], etapas: [], categorias: [] });
  const [cargando, setCargando] = useState(false);
  const [sinConexion, setSinConexion] = useState(false);

  // Las peticiones tardan lo que tarde Odoo: si el usuario cambia de filtro
  // rápido, solo debe pintarse la respuesta de la última.
  const peticion = useRef(0);

  const manejar = (e) => {
    if (/conectado|caducado/i.test(e.message || "")) { setSinConexion(true); return; }
    showToast(e.message || "Error al consultar las incidencias", "error");
  };

  const loadMeta = useCallback(async () => {
    if (!token) return;
    try { setMeta(await api.getHelpdeskMeta()); setSinConexion(false); }
    catch (e) { manejar(e); }
  }, [token]); // eslint-disable-line

  const loadTickets = useCallback(async (filtros = {}) => {
    if (!token) return;
    const mio = ++peticion.current;
    setCargando(true);
    try {
      const r = await api.getHelpdeskTickets(filtros);
      if (mio !== peticion.current) return;   // llegó tarde: la descarto
      setTickets(r.tickets || []);
      setTotal(r.total || 0);
      setSinConexion(false);
    } catch (e) {
      if (mio === peticion.current) manejar(e);
    } finally {
      if (mio === peticion.current) setCargando(false);
    }
  }, [token]); // eslint-disable-line

  const loadKanban = useCallback(async (filtros = {}) => {
    if (!token) return;
    const mio = ++peticion.current;
    setCargando(true);
    try {
      const r = await api.getHelpdeskKanban(filtros);
      if (mio !== peticion.current) return;
      setKanban(r);
      setSinConexion(false);
    } catch (e) {
      if (mio === peticion.current) manejar(e);
    } finally {
      if (mio === peticion.current) setCargando(false);
    }
  }, [token]); // eslint-disable-line

  const [usuarios, setUsuarios] = useState([]);

  const loadUsuarios = useCallback(async () => {
    if (!token) return;
    try { setUsuarios(await api.getHelpdeskUsuarios()); } catch { setUsuarios([]); }
  }, [token]); // eslint-disable-line

  // Devuelve el ticket actualizado, o null si Odoo lo deniega.
  const actualizar = async (id, cambios) => {
    try {
      const t = await api.actualizarTicket(id, cambios);
      setTickets(p => p.map(x => x.id === id ? t : x));
      setKanban(k => ({ ...k, tickets: k.tickets.map(x => x.id === id ? t : x) }));
      showToast("Incidencia actualizada");
      return t;
    } catch (e) {
      showToast(e.message || "No se pudo actualizar la incidencia", "error");
      return null;
    }
  };

  const loadMensajes = async (id) => {
    try { return await api.getHelpdeskMensajes(id); }
    catch { return []; }   // sin permiso sobre mail.message se pierde el hilo, no el detalle
  };

  const responder = async (id, cuerpo, interno) => {
    try {
      const r = await api.responderTicket(id, cuerpo, interno);
      if (interno) showToast("Nota interna anadida");
      // Un mensaje sin destinatarios entra en el hilo igual, asi que parece
      // enviado. Odoo no notifica al autor de su propio mensaje: si el contacto
      // del ticket eres tu, no sale ningun correo.
      else if (r.notificados === 0)
        showToast("Publicado en el hilo, pero no se ha notificado a nadie por correo", "warn");
      else showToast(r.notificados
        ? `Respuesta enviada a ${r.notificados} destinatario${r.notificados === 1 ? "" : "s"}`
        : "Respuesta enviada al cliente");
      return true;
    } catch (e) {
      showToast(e.message || "No se pudo enviar el mensaje", "error");
      return false;
    }
  };

  const loadAdjuntos = async (id) => {
    try { return await api.getHelpdeskAdjuntos(id); }
    catch { return []; }   // sin permiso sobre ir.attachment no es un error que mostrar
  };

  const loadTicket = async (id) => {
    try { return await api.getHelpdeskTicket(id); }
    catch (e) { manejar(e); return null; }
  };

  return { tickets, total, kanban, meta, usuarios, cargando, sinConexion,
           loadMeta, loadTickets, loadKanban, loadTicket, loadAdjuntos, loadMensajes,
           loadUsuarios, actualizar, responder };
}
